import * as React from 'react';
import { memo } from 'react';

import URI from 'jsuri';
import { compose } from 'recompose';

import Imgix from 'js/components/Imgix';
import imgix from 'js/lib/imgix';
import { isRightToLeft } from 'js/lib/language';

import { ProductCard } from '@coursera/coursera-ui';
import { ProductCardPlaceholder } from '@coursera/coursera-ui/lib/components/coursera/ProductCard';

// Save/Unsave Experiment
import SavingBookmark from 'bundles/browse/components/SavingBookmark';
import { getProductCardTextColor } from 'bundles/browse/components/utils/BookmarkUtils';
import {
  PRODUCT_CARD_HEIGHT,
  PRODUCT_CARD_HEIGHT_MOBILE,
  PRODUCT_CARD_SQUARE_PARTNER_LOGO_SIDE_LENGTH,
  PRODUCT_CARD_WIDTH,
} from 'bundles/browse/constants';
import type { SavedProductStringTypes } from 'bundles/browse/types/SavedProductQueries';
import type { PropsFromWithMediaMaxWidthScreenTablet } from 'bundles/browse/utils';
import { getProductCardDisplayProps, isProductS12n, withMediaMaxWidthScreenTablet } from 'bundles/browse/utils';
import { buildNewWindowOnClickViaUrl } from 'bundles/common/utils/newWindowUtils';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import type { Product } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { getGenAiBadges } from 'bundles/product-card/hooks/useGenAiBadges';
import { getSaveTypeForS12nProductVariant } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import { savedProductTypes } from 'bundles/unified-description-page-common/constants';

import _t from 'i18n!nls/browse';

import 'css!./__styles__/BrowseProductCard';

type TrackingData = Record<string, unknown>;

type PropsForSavedExperiment = {
  saved?: boolean;
  loadingSavedProducts?: boolean;
};

type PropsNowAccessedForSavedExperiment = {
  isRhymeProject?: boolean;
  productVariant?: string;
};

type ProductProps = Product & {
  logo: string;
  photoUrl: string;
  premiumExperienceVariant: null | 'PremiumCourse' | 'PremiumGrading' | 'BaseVariant';
  score: number;
  __typename: string;
  onDemandSpecializationId: string;
  isPartOfCourseraPlus: boolean | null;
  isFreeCertificate: boolean | null;
};

export type PropsFromCaller = {
  tag?: string;
  tagClassNames?: string;
  queryParameters?: { [index: string]: string };
  product: ProductProps;
  isLoading?: boolean;
  trackingData?: TrackingData;
  primaryLanguages?: Array<string>;
  forceOpenInNewTab?: boolean; // Temporary Param for: https://coursera.atlassian.net/browse/GR-21263
  isInteractive?: boolean;
  showFreeBadge?: boolean;
  alwaysShowCourseraPlusPill?: boolean;
} & PropsForSavedExperiment &
  PropsNowAccessedForSavedExperiment;

type PropsToComponent = PropsFromCaller &
  PropsFromWithMediaMaxWidthScreenTablet &
  PropsFromCourseraPlusProductOwnerships;

/** @deprecated replaced by the new ProductCardV2, do not use this component at all cost */
export class BrowseProductCard extends React.PureComponent<PropsToComponent> {
  static defaultProps = {
    isInteractive: true,
  };

  renderProductCardSubtitle() {
    const { product, ownsCourseraPlus, alwaysShowCourseraPlusPill } = this.props;

    if (!product) {
      return null;
    }

    const partnerName = product.partners?.elements?.[0]?.name ?? null;
    const showCourseraPlusPill = (ownsCourseraPlus && product.isPartOfCourseraPlus) || alwaysShowCourseraPlusPill;
    const courseraPlusLogoSrc = product.__typename === 'CoursesV1' ? logo.PLUS_PILL_BLUE : logo.PLUS_PILL_WHITE;

    return showCourseraPlusPill ? (
      <div>
        <div className="partner-name">{partnerName}</div>
        <div className="coursera-plus-pill">
          <Imgix src={courseraPlusLogoSrc} maxWidth={32} maxHeight={14} alt={_t('Coursera Plus')} />
        </div>
      </div>
    ) : (
      partnerName
    );
  }

  render() {
    const {
      matches,
      product,
      trackingData,
      isLoading,
      tag,
      tagClassNames,
      primaryLanguages,
      forceOpenInNewTab,
      queryParameters,
      isInteractive,
      showFreeBadge,
      productVariant,
      isRhymeProject,
      saved,
      loadingSavedProducts,
    } = this.props;

    const { badges } = getGenAiBadges(product?.slug);

    if (!product) return null;
    if (isLoading) {
      return <BrowseProductCardPlaceholder />;
    }
    const cardHeight = matches ? PRODUCT_CARD_HEIGHT_MOBILE : PRODUCT_CARD_HEIGHT;
    const cardSize = matches ? 'sm' : 'lg';
    const isS12n = isProductS12n(product);
    const computedType = isS12n ? 's12n' : 'course';

    let computedSaveType = savedProductTypes.COURSE as SavedProductStringTypes;
    if (isS12n) {
      computedSaveType = getSaveTypeForS12nProductVariant(productVariant);
    }

    if (isRhymeProject) computedSaveType = savedProductTypes.GUIDED_PROJECT as SavedProductStringTypes;

    const { cardHref, gradient, label } = getProductCardDisplayProps(product, isS12n);
    const cardHrefWithQueryParamsUri = new URI(cardHref);

    if (queryParameters) {
      Object.entries(queryParameters).forEach((param) => {
        cardHrefWithQueryParamsUri.addQueryParam(param[0], param[1]);
      });
    }

    const cardHrefWithQueryParamsUrl = cardHrefWithQueryParamsUri.toString();

    const imageUrl = isS12n ? product.logo : product.photoUrl;
    const isImageGif = imageUrl && imageUrl.substring(imageUrl.length - 3).toLowerCase() === 'gif';
    const gifCompressionOptions = { auto: 'compress', format: 'jpg' };
    const imageSrc = imgix.processImage(imageUrl, {
      width: PRODUCT_CARD_WIDTH,
      ...(isImageGif ? gifCompressionOptions : { auto: 'compress' }),
    });
    const logoSrc = imgix.processImage(product.partners?.elements?.[0]?.squareLogo ?? undefined, {
      width: PRODUCT_CARD_SQUARE_PARTNER_LOGO_SIDE_LENGTH,
      height: PRODUCT_CARD_SQUARE_PARTNER_LOGO_SIDE_LENGTH,
      auto: 'compress',
    });
    const titleLang = primaryLanguages && primaryLanguages.length === 1 ? primaryLanguages[0] : undefined;

    const showGradient = isS12n;

    const shouldShowFreeBadgeLogic =
      (showFreeBadge &&
        !showGradient &&
        (product.premiumExperienceVariant === 'BaseVariant' || !product.premiumExperienceVariant)) ||
      product.isFreeCertificate;
    let badge: string | JSX.Element | undefined = shouldShowFreeBadgeLogic ? _t('Free') : undefined;

    // These are exclusively for Professional Certs and should not conflict with Free
    if (badges.length) {
      badge = badges.at(-1);
    }

    const TrackedContainer = isInteractive
      ? (TrackedLink2 as React.ComponentClass<React.ComponentProps<typeof TrackedLink2>>)
      : (TrackedDiv as React.ComponentClass<React.ComponentProps<typeof TrackedDiv>>);

    return (
      <div
        data-e2e={isS12n ? 'browse-s12n-card' : 'browse-course-card'}
        data-testid={isS12n ? 'browse-s12n-card' : 'browse-course-card'}
        className="rc-BrowseProductCard"
        style={{ width: PRODUCT_CARD_WIDTH, minHeight: cardHeight }}
      >
        <TrackedContainer
          trackingName="collection_product_card"
          data={trackingData}
          className="nostyle collection-product-card non-cds"
          aria-label={`${product.name}. ${this.renderProductCardSubtitle()}. ${label}`}
          lang={titleLang}
          withVisibilityTracking={true}
          tabIndex={0}
          {...(isInteractive
            ? {
                href: cardHrefWithQueryParamsUrl,
                onClick: forceOpenInNewTab
                  ? buildNewWindowOnClickViaUrl({ href: cardHrefWithQueryParamsUrl })
                  : undefined,
                // Temporary onClickHandler for: https://coursera.atlassian.net/browse/GR-21263
              }
            : // we have to set aria-label to undefined if we want to disable tab navigation
              // on this component (with tabIndex: -1), otherwise, the browser will always override
              // tabIndex to 0 to make the div element tab-accessible.
              { 'aria-label': undefined, tabIndex: -1 })}
        >
          <ProductCard
            id={product.id}
            badge={badge}
            isS12n={isS12n}
            gradient={gradient}
            imageSrc={imageSrc}
            logoSrc={logoSrc}
            title={product.name}
            titleLang={titleLang}
            subtitle={this.renderProductCardSubtitle()}
            isInteractive={isInteractive}
            showGradient={showGradient}
            type={computedType}
            style={{ borderRadius: 3, minHeight: cardHeight - 5, height: 'auto' }}
            size={cardSize}
            label={label}
            tag={tag}
            actionsElement={null}
            tagClassNames={tagClassNames}
            dir={isRightToLeft(_t.getLocale()) ? 'rtl' : 'ltr'}
            useDivForTitleElement
          />
          {!loadingSavedProducts && (
            <SavingBookmark
              saved={saved}
              color={getProductCardTextColor(computedType)}
              product={{ product_id: product.id, product_type: computedSaveType }}
              isRightAligned={true}
            />
          )}
        </TrackedContainer>
      </div>
    );
  }
}

/** @deprecated replaced by the new ProductCardV2, do not use this component at all cost */
export const BrowseProductCardPlaceholderRender = memo(({ matches }: PropsFromWithMediaMaxWidthScreenTablet) => {
  return matches ? (
    <div
      className="rc-BrowseProductCard"
      data-testid="browse-product-card-placeholder"
      style={{ position: 'relative', width: PRODUCT_CARD_WIDTH, height: PRODUCT_CARD_HEIGHT_MOBILE }}
    >
      <ProductCardPlaceholder size="sm" />
    </div>
  ) : (
    <div
      className="rc-BrowseProductCard"
      data-testid="browse-product-card-placeholder"
      style={{ position: 'relative', width: PRODUCT_CARD_WIDTH, height: PRODUCT_CARD_HEIGHT }}
    >
      <ProductCardPlaceholder />
    </div>
  );
});
/** @deprecated replaced by the new ProductCardV2, do not use this component at all cost */
export const BrowseProductCardPlaceholder = withMediaMaxWidthScreenTablet(BrowseProductCardPlaceholderRender);

/** @deprecated replaced by the new ProductCardV2, do not use this component at all cost */
export default memo(
  compose<PropsToComponent, PropsFromCaller>(
    withCourseraPlusProductOwnerships(),
    withMediaMaxWidthScreenTablet
  )(BrowseProductCard)
);
